import { computed, signal } from '@angular/core';

import { Role } from '@tourfold/libraries/tf-api-client-public-v2';

import {
  AdvancedFilter,
  AdvancedGrantRow,
  EditorMode,
  EditorTab,
} from './role-editor.models';
import {
  ROLE_PERMISSION_GROUPS,
  ROLE_PERMISSION_GROUP_BY_ID,
  PermissionGroupDisplayLevel,
  PermissionGroupId,
  PermissionLevel,
  buildGroupDisplayLevelsFromGrants,
  reconcileGroupDisplayLevelsAfterGrantToggle,
  replaceGroupLevelInGrantSet,
} from './role-permission-groups';

export interface RoleEditorSnapshot {
  readonly id: string | null;
  readonly name: string;
  readonly description: string;
  readonly grants: readonly string[];
  readonly assignableByRoles: readonly string[];
}

const ALL_GROUP_DISPLAY_LEVELS_NONE = Object.fromEntries(
  ROLE_PERMISSION_GROUPS.map((group) => [group.id, 'none'])
) as Record<PermissionGroupId, PermissionGroupDisplayLevel>;

export class RoleEditorState {
  constructor(
    private readonly filterAssignableRoleIds: (ids: readonly string[]) => string[],
    private readonly createEmptyDraft: () => Role
  ) {}

  readonly mode = signal<EditorMode>('edit');
  readonly sourceRole = signal<Role | null>(null);
  readonly originalSnapshot = signal<RoleEditorSnapshot | null>(null);
  readonly name = signal('');
  readonly description = signal('');
  readonly assignableByRoles = signal<string[]>([]);
  readonly enabledGrantSet = signal<ReadonlySet<string>>(new Set());
  readonly groupDisplayLevels = signal<Record<PermissionGroupId, PermissionGroupDisplayLevel>>({
    ...ALL_GROUP_DISPLAY_LEVELS_NONE,
  });
  readonly activeTab = signal<EditorTab>('permissions');
  readonly advancedSearchQuery = signal('');
  readonly advancedFilter = signal<AdvancedFilter>('all');
  readonly expandedAdvancedGroupIds = signal<ReadonlySet<PermissionGroupId>>(new Set());

  readonly isOpen = computed(() => this.mode() === 'create' || this.sourceRole() !== null);

  readonly selectedGrantList = computed<readonly string[]>(() =>
    [...this.enabledGrantSet()].sort((a, b) => a.localeCompare(b))
  );

  readonly currentSnapshot = computed<RoleEditorSnapshot | null>(() => {
    const source = this.sourceRole();
    if (!this.isOpen()) {
      return null;
    }
    return {
      id: source?.id ?? null,
      name: this.name().trim(),
      description: normalizeDescription(this.description()),
      grants: this.selectedGrantList(),
      assignableByRoles: this.assignableByRoles().slice().sort((a, b) => a.localeCompare(b)),
    };
  });

  readonly hasChanges = computed(() => {
    const original = this.originalSnapshot();
    const current = this.currentSnapshot();
    if (!current) {
      return false;
    }
    if (this.mode() === 'create') {
      if (!original) {
        return false;
      }
      return (
        current.name.length > 0 ||
        current.description.length > 0 ||
        JSON.stringify(current.grants) !== JSON.stringify(original.grants) ||
        JSON.stringify(current.assignableByRoles) !== JSON.stringify(original.assignableByRoles)
      );
    }
    return !!original && JSON.stringify(original) !== JSON.stringify(current);
  });

  openForCreate(defaultGroupLevels: Readonly<Record<PermissionGroupId, PermissionLevel>>): void {
    this.applyRole(this.createEmptyDraft(), 'create', defaultGroupLevels, /* blankName */ true);
  }

  openFromRole(role: Role, mode: EditorMode): void {
    this.applyRole(role, mode, undefined, /* blankName */ false);
  }

  private applyRole(
    sourceRole: Role,
    mode: EditorMode,
    defaultGroupLevels: Readonly<Record<PermissionGroupId, PermissionLevel>> | undefined,
    blankName: boolean
  ): void {
    const grants = sourceRole.grants ?? [];

    this.mode.set(mode);
    this.sourceRole.set(sourceRole);
    this.name.set(blankName ? '' : (sourceRole.name ?? ''));
    this.description.set(sourceRole.description ?? '');
    this.assignableByRoles.set(
      this.filterAssignableRoleIds([...(sourceRole.assignableByRoles ?? [])])
    );
    this.enabledGrantSet.set(new Set(grants));
    this.groupDisplayLevels.set(
      defaultGroupLevels
        ? { ...(defaultGroupLevels as Record<PermissionGroupId, PermissionGroupDisplayLevel>) }
        : buildGroupDisplayLevelsFromGrants(grants)
    );
    this.activeTab.set('permissions');
    this.expandedAdvancedGroupIds.set(new Set());

    this.originalSnapshot.set({
      id: mode === 'create' ? null : sourceRole.id,
      name: this.name().trim(),
      description: normalizeDescription(this.description()),
      grants: [...grants].sort((a, b) => a.localeCompare(b)),
      assignableByRoles: this.filterAssignableRoleIds([
        ...(sourceRole.assignableByRoles ?? []),
      ]).sort((a, b) => a.localeCompare(b)),
    });
  }

  close(): void {
    this.mode.set('edit');
    this.sourceRole.set(null);
    this.originalSnapshot.set(null);
    this.name.set('');
    this.description.set('');
    this.assignableByRoles.set([]);
    this.enabledGrantSet.set(new Set());
    this.groupDisplayLevels.set({ ...ALL_GROUP_DISPLAY_LEVELS_NONE });
    this.activeTab.set('permissions');
    this.expandedAdvancedGroupIds.set(new Set());
  }

  selectGroupLevel(groupId: PermissionGroupId, level: PermissionLevel): void {
    const group = ROLE_PERMISSION_GROUP_BY_ID[groupId];
    if (!group.levels.includes(level)) {
      return;
    }
    this.enabledGrantSet.update((grants) =>
      replaceGroupLevelInGrantSet(group, level, grants, this.groupDisplayLevels())
    );
    this.groupDisplayLevels.update((levels) => ({ ...levels, [groupId]: level }));
  }

  setGrantEnabled(grant: string, enabled: boolean): void {
    this.enabledGrantSet.update((grants) => {
      const next = new Set(grants);
      if (enabled) {
        next.add(grant);
      } else {
        next.delete(grant);
      }
      return next;
    });
    this.groupDisplayLevels.update((levels) =>
      reconcileGroupDisplayLevelsAfterGrantToggle(grant, enabled, this.enabledGrantSet(), levels)
    );
  }

  toggleAdvancedGrant(row: AdvancedGrantRow, checked: boolean): void {
    if (!row.assignable) {
      return;
    }
    this.setGrantEnabled(row.grant, checked);
  }

  toggleAdvancedGrantGroup(groupId: PermissionGroupId): void {
    this.expandedAdvancedGroupIds.update((groupIds) => {
      const next = new Set(groupIds);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }

  isAdvancedGrantGroupExpanded(groupId: PermissionGroupId): boolean {
    return this.expandedAdvancedGroupIds().has(groupId);
  }
}

function normalizeDescription(description: string | undefined | null): string {
  return description?.trim() ?? '';
}
